/* ============================================================
   dbakers Signature Cake Builder JS — Accordion Style
   ADA compliant — aria-expanded, aria-live, disabled guards

   Variant ID lookups come from window.dbsb*Variants and
   window.dbsbAddonIds (exported from Liquid). No hardcoded IDs in
   this file — if a Shopify product or variant changes, the Liquid
   regenerates the ID and this JS picks it up on next page load.

   ── 2026-05-21 — Companion-line fix ──
   Submit pattern now matches the designer cake builder:
   1. Cake adds first as its own fetch. Shopify returns the cake's
      unique line key.
   2. Add-ons fetch second, each tagged _addon_for: <cake_key>.
      Since the key is unique per submit, identical add-ons across
      multiple cakes no longer merge into qty 2 — they remain as
      separately-attributed line items.
   3. Errors are surfaced on either step. On cake-add failure we
      alert and reset the button. On addon-add failure we alert but
      leave the cake in cart (matches designer behavior — partial
      success is preferable to losing the whole order). Customer
      can refresh cart and contact us if extras went missing.
   4. r.ok checked explicitly on both fetches — protects against
      future variant unpublishing failing silently.
   5. Verify step compares builder-promised total to actual cart
      total and logs any mismatch to the console. Helps catch
      add-on variant resolution issues without breaking checkout.

   Plus: redirect to /cart on success (matches designer + cupcakes).

   ── Cake-style step ──
   "Choose your cake" (Birthday / Red Velvet / Carrot) is a single-select
   radio group. Paid Birthday options (Funfetti, Rainbow) carry a
   data-flavor matching a designer_-_heart_cake_flavors variant title;
   that variant is pushed as the upcharge line via window.dbsbFlavorVariants,
   exactly like the heart/designer builders. Free options (Red Velvet,
   Carrot) are property-only — their flavor premium is already in the base
   price, so no variant is attached (avoids double-charging). The step
   slots between diet and special; openNextAfterDiet() routes to it when
   present, else straight to special.
   ============================================================ */

(function () {
  'use strict';

  /* Pull variant ID maps from Liquid (set in dbakers-signature-builder.liquid).
     Defensive defaults in case a map is missing — the affected addon
     simply won't add to cart, but the rest of the builder still works. */
  var ADDON_IDS    = window.dbsbAddonIds        || {};
  var PLAQUE_IDS   = window.dbsbPlaqueVariants  || {};
  var TOPPER_IDS   = window.dbsbTopperVariants  || {};
  var CANDLE_IDS   = window.dbsbCandleVariants  || {};
  var METALLIC_IDS = window.dbsbMetallicVariants || {};
  var FLAVOR_IDS   = window.dbsbFlavorVariants  || {};

  var state = {
    variantId: null,
    basePrice: 0,
    sizeName: '',
    cakeStyle: { value: '', flavorTitle: '', price: 0 },
    diet: { gf: false, nut: false },
    nutAcknowledged: false,
    gfAcknowledged: false,
    addons: {
      message:  { selected: false, color: '', text: '' },
      candles:  {
        selected: false,
        /* Each type independent — customer can have all 4 active.
           Mirrors designer cake builder's state shape. */
        types: {
          rainbow:   { active: false },
          sparklers: { active: false },
          metallic:  { active: false, color: '' },
          number:    { active: false, color: '', number: '' }
        }
      },
      topper:   { selected: false, color: '' },
      gift:     { selected: false, text: '' }
    },
    extras: {
      bow:      { selected: false, price: 5.25 },
      server:   { selected: false, price: 2.00 },
      coldpack: { selected: false, price: 3.00 }
    }
  };

  /* ── Init ── */
  function init() {
    if (!document.getElementById('dbsb-root')) return;
    bindSectionHeaders();
    bindSizeCards();
    bindDietCards();
    bindDietSkip();
    bindNutAck();
    bindGfAck();
    bindVeganCard();
    bindAddonRows();
    bindCandleTypes();
    bindImageTiles();
    bindMsgInput();
    bindGiftInput();
    bindCandleNumber();
    bindExtraCards();
    bindCakeStyleCards();
    bindStickyBtn();
    updateSticky();
    openSection('size');
  }

  if (document.readyState !== 'loading') {
    setTimeout(init, 300);
  } else {
    document.addEventListener('DOMContentLoaded', function () { setTimeout(init, 300); }, { once: true });
  }

  /* ── Section accordion ── */
  function openSection(id) {
    var body = document.getElementById('dbsb-body-' + id);
    var header = document.querySelector('[data-section="' + id + '"]');
    if (body) body.classList.add('is-open');
    if (header) header.setAttribute('aria-expanded', 'true');
  }

  function closeSection(id) {
    var body = document.getElementById('dbsb-body-' + id);
    var header = document.querySelector('[data-section="' + id + '"]');
    if (body) body.classList.remove('is-open');
    if (header) header.setAttribute('aria-expanded', 'false');
  }

  /* After the dietary step is satisfied, open the cake-style step when the
     product has one (Birthday / Red Velvet / Carrot); otherwise go straight
     to "Make it special". */
  function openNextAfterDiet() {
    if (document.getElementById('dbsb-section-cakestyle')) {
      openSection('cakestyle');
    } else {
      openSection('special');
    }
  }

  function bindSectionHeaders() {
    document.querySelectorAll('.dbsb-section-header').forEach(function (header) {
      header.addEventListener('click', function (e) {
        e.stopPropagation();
        var id   = this.dataset.section;
        var body = document.getElementById('dbsb-body-' + id);
        if (body) {
          body.classList.toggle('is-open');
          this.setAttribute('aria-expanded', body.classList.contains('is-open') ? 'true' : 'false');
        }
      });
      header.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.click(); }
      });
    });
  }

  /* ── Size cards ── */
  function bindSizeCards() {
    document.querySelectorAll('.dbsb-size-card').forEach(function (card) {
      card.addEventListener('click', function (e) {
        e.preventDefault(); e.stopPropagation();
        if (this.disabled) return;
        document.querySelectorAll('.dbsb-size-card').forEach(function (c) {
          c.classList.remove('is-selected');
          c.setAttribute('aria-checked', 'false');
        });
        this.classList.add('is-selected');
        this.setAttribute('aria-checked', 'true');
        state.variantId = this.dataset.variantId;
        state.basePrice = parseFloat(this.dataset.variantPrice) / 100;
        state.sizeName  = this.dataset.variantTitle;
        syncVariantInput();
        markSectionDone('size');
        var stickyBtn = document.getElementById('dbsb-sticky-btn');
        if (stickyBtn) stickyBtn.classList.add('is-active');
        updateSticky();
        closeSection('size');
        openSection('diet');
      });
      card.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.click(); }
      });
    });
  }

  /* ── Diet cards — multi-select checkboxes ── */
  function bindDietCards() {
    document.querySelectorAll('.dbsb-diet-card').forEach(function (card) {
      card.addEventListener('click', function (e) {
        e.preventDefault(); e.stopPropagation();
        if (this.disabled || this.classList.contains('is-unavailable')) return;
        if (!this.dataset.diet) return;

        var diet = this.dataset.diet;
        var isSelected = this.classList.contains('is-selected');
        this.classList.toggle('is-selected', !isSelected);
        this.setAttribute('aria-checked', String(!isSelected));

        if (diet === 'gf') {
          state.diet.gf = !isSelected;
          state.gfAcknowledged = false;
        }
        if (diet === 'nut') {
          state.diet.nut = !isSelected;
          state.nutAcknowledged = false;
        }

        var warn = document.getElementById('dbsb-nut-warning');
        if (state.diet.nut) {
          if (warn) warn.classList.add('is-visible');
          var box = document.getElementById('dbsb-nut-box');
          if (box) box.classList.remove('is-checked');
        } else {
          if (warn) warn.classList.remove('is-visible');
        }

        var gfWarn = document.getElementById('dbsb-gf-warning');
        var gfReview = document.getElementById('dbsb-gf-review');
        if (state.diet.gf) {
          if (gfWarn) gfWarn.classList.add('is-visible');
          if (gfReview) gfReview.classList.add('is-visible');
          var gfBox = document.getElementById('dbsb-gf-box');
          if (gfBox) gfBox.classList.remove('is-checked');
        } else {
          if (gfWarn) gfWarn.classList.remove('is-visible');
          if (gfReview) gfReview.classList.remove('is-visible');
          state.gfAcknowledged = false;
        }

        var nutOk = !state.diet.nut || state.nutAcknowledged;
        var gfOk  = !state.diet.gf  || state.gfAcknowledged;
        if (nutOk && gfOk) {
          markSectionDone('diet');
          updateSticky();
          openNextAfterDiet();
        }
        updateSticky();
      });
      card.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.click(); }
      });
    });
  }

  /* ── Diet skip button ── */
  function bindDietSkip() {
    var skip = document.getElementById('dbsb-diet-skip');
    if (!skip) return;
    skip.addEventListener('click', function(e) {
      e.preventDefault(); e.stopPropagation();
      document.querySelectorAll('.dbsb-diet-card').forEach(function(c) {
        c.classList.remove('is-selected');
        c.setAttribute('aria-checked', 'false');
      });
      state.diet.gf = false;
      state.diet.nut = false;
      state.nutAcknowledged = false;
      state.gfAcknowledged = false;
      var warn = document.getElementById('dbsb-nut-warning');
      if (warn) warn.classList.remove('is-visible');
      var gfWarn = document.getElementById('dbsb-gf-warning');
      if (gfWarn) gfWarn.classList.remove('is-visible');
      var gfReview = document.getElementById('dbsb-gf-review');
      if (gfReview) gfReview.classList.remove('is-visible');
      var body = document.getElementById('dbsb-body-diet');
      var header = document.querySelector('[data-section="diet"]');
      if (body) body.classList.remove('is-open');
      if (header) header.setAttribute('aria-expanded', 'false');
      markSectionDone('diet');
      updateSticky();
      openNextAfterDiet();
    });
    skip.addEventListener('keydown', function(e) {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.click(); }
    });
  }

  /* ── Nut acknowledgment checkbox ── */
  function bindNutAck() {
    var ack = document.getElementById('dbsb-nut-ack');
    if (!ack) return;

    function handleNutAck(e) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      var box = document.getElementById('dbsb-nut-box');
      if (!box) return;
      box.classList.toggle('is-checked');
      state.nutAcknowledged = box.classList.contains('is-checked');
      ack.setAttribute('aria-checked', String(state.nutAcknowledged));
      if (state.nutAcknowledged) {
        markSectionDone('diet');
        updateSticky();
        openNextAfterDiet();
      }
      updateSticky();
    }

    /* mousedown + touchstart fire before Dawn's click handlers */
    ack.addEventListener('mousedown', handleNutAck);
    ack.addEventListener('touchstart', handleNutAck, { passive: false });
    ack.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleNutAck(e); }
    });
  }

  /* ── GF acknowledgment checkbox ── */
  function bindGfAck() {
    var ack = document.getElementById('dbsb-gf-ack');
    if (!ack) return;

    function handleGfAck(e) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      var box = document.getElementById('dbsb-gf-box');
      if (!box) return;
      box.classList.toggle('is-checked');
      state.gfAcknowledged = box.classList.contains('is-checked');
      ack.setAttribute('aria-checked', String(state.gfAcknowledged));
      if (state.gfAcknowledged) {
        markSectionDone('diet');
        updateSticky();
        openNextAfterDiet();
      }
      updateSticky();
    }

    ack.addEventListener('mousedown', handleGfAck);
    ack.addEventListener('touchstart', handleGfAck, { passive: false });
    ack.addEventListener('keydown', function(e) {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleGfAck(e); }
    });
  }

  /* ── Vegan card toggle ── */
  function bindVeganCard() {
    var card  = document.getElementById('dbsb-vegan-card');
    var panel = document.getElementById('dbsb-vegan-panel');
    if (!card || !panel) return;
    function handleVegan(e) {
      e.preventDefault(); e.stopPropagation();
      var isOpen = card.classList.contains('is-expanded');
      card.classList.toggle('is-expanded', !isOpen);
      panel.classList.toggle('is-visible', !isOpen);
      panel.setAttribute('aria-hidden', String(isOpen));
      card.setAttribute('aria-expanded', String(!isOpen));
    }
    card.addEventListener('mousedown', handleVegan);
    card.addEventListener('touchstart', handleVegan, { passive: false });
    card.addEventListener('keydown', function(e) {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleVegan(e); }
    });
  }

  /* ── Addon rows ── */
  function bindAddonRows() {
    document.querySelectorAll('.dbsb-addon-row').forEach(function (row) {
      row.addEventListener('click', function (e) {
        e.preventDefault(); e.stopPropagation();
        var id    = this.dataset.addon;
        var card  = document.getElementById('dbsb-addon-card-' + id);
        var panel = document.getElementById('dbsb-sub-panel-' + id);
        if (!card) return;
        var isOpen = card.classList.contains('is-selected');
        card.classList.toggle('is-selected', !isOpen);
        if (panel) {
          panel.classList.toggle('is-open', !isOpen);
          panel.setAttribute('aria-hidden', String(isOpen));
        }
        this.setAttribute('aria-expanded', String(!isOpen));
        if (state.addons[id]) state.addons[id].selected = !isOpen;
        if (isOpen) resetAddon(id);
        updateSticky();
      });
      row.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.click(); }
      });
    });
  }

  function resetAddon(id) {
    if (id === 'message') {
      state.addons.message.color = state.addons.message.text = '';
      document.querySelectorAll('[data-tile-group="msg-color"]').forEach(function (t) { t.classList.remove('is-selected'); });
      var wrap = document.getElementById('dbsb-msg-input-wrap');
      if (wrap) wrap.classList.remove('is-visible');
      var inp  = document.getElementById('dbsb-msg-text');
      if (inp)  inp.value = '';
      var cnt  = document.getElementById('dbsb-msg-char-count');
      if (cnt)  cnt.textContent = '0';
    }
    if (id === 'candles') {
      state.addons.candles.types.rainbow.active   = false;
      state.addons.candles.types.sparklers.active = false;
      state.addons.candles.types.metallic.active  = false;
      state.addons.candles.types.metallic.color   = '';
      state.addons.candles.types.number.active    = false;
      state.addons.candles.types.number.color     = '';
      state.addons.candles.types.number.number    = '';
      document.querySelectorAll('.dbsb-type-pill').forEach(function (p) {
        p.classList.remove('is-selected');
        p.setAttribute('aria-checked', 'false');
      });
      document.querySelectorAll('.dbsb-candle-sub').forEach(function (el) {
        el.classList.remove('is-visible');
      });
      document.querySelectorAll('[data-tile-group^="candle-color-"]').forEach(function (t) {
        t.classList.remove('is-selected');
        t.setAttribute('aria-checked', 'false');
      });
      var cn = document.getElementById('dbsb-candle-number');
      if (cn) cn.value = '';
    }
    if (id === 'topper') {
      state.addons.topper.color = '';
      document.querySelectorAll('[data-tile-group="topper-color"]').forEach(function (t) { t.classList.remove('is-selected'); });
    }
    if (id === 'gift') {
      state.addons.gift.text = '';
      var ta = document.getElementById('dbsb-gift-text');
      if (ta) ta.value = '';
    }
  }

  /* ── Candle type pills — multi-select, mirrors designer ── */
  function bindCandleTypes() {
    document.querySelectorAll('.dbsb-type-pill').forEach(function (pill) {
      pill.addEventListener('click', function (e) {
        e.preventDefault(); e.stopPropagation();
        var type = this.dataset.type;
        var typeState = state.addons.candles.types[type];
        if (!typeState) return;

        /* Toggle THIS type only — other types stay as they are. Customer
           can mix and match (rainbow + sparklers + metallic gold etc). */
        typeState.active = !typeState.active;
        this.classList.toggle('is-selected', typeState.active);
        this.setAttribute('aria-checked', typeState.active ? 'true' : 'false');

        var sub = document.getElementById('dbsb-candle-sub-' + type);
        if (sub) sub.classList.toggle('is-visible', typeState.active);

        /* Clear color/number when deactivating so the cart doesn't keep
           stale data from a deselected type. */
        if (!typeState.active) {
          if (type === 'metallic') typeState.color = '';
          if (type === 'number')   { typeState.color = ''; typeState.number = ''; }
          document.querySelectorAll('[data-tile-group="candle-color-' + type + '"]').forEach(function (t) {
            t.classList.remove('is-selected');
            t.setAttribute('aria-checked', 'false');
          });
          if (type === 'number') {
            var cn = document.getElementById('dbsb-candle-number');
            if (cn) cn.value = '';
          }
        }
        updateSticky();
      });
      pill.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.click(); }
      });
    });
  }

  /* ── Image tiles ── */
  function bindImageTiles() {
    document.querySelectorAll('.dbsb-img-tile').forEach(function (tile) {
      tile.addEventListener('click', function (e) {
        e.preventDefault(); e.stopPropagation();
        var group = this.dataset.tileGroup;
        if (!group) return;
        document.querySelectorAll('[data-tile-group="' + group + '"]').forEach(function (t) {
          t.classList.remove('is-selected');
          t.setAttribute('aria-checked', 'false');
        });
        this.classList.add('is-selected');
        this.setAttribute('aria-checked', 'true');
        var color = this.dataset.tileValue;
        if (group === 'msg-color') {
          state.addons.message.color = color;
          var wrap = document.getElementById('dbsb-msg-input-wrap');
          if (wrap) {
            wrap.classList.add('is-visible');
            var inp = document.getElementById('dbsb-msg-text');
            if (inp) setTimeout(function () { inp.focus(); }, 100);
          }
        } else if (group === 'candle-color-metallic') {
          state.addons.candles.types.metallic.color = color;
          updateSticky();
        } else if (group === 'candle-color-number') {
          state.addons.candles.types.number.color = color;
          updateSticky();
        } else if (group === 'topper-color') {
          state.addons.topper.color = color;
        }
      });
      tile.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.click(); }
      });
    });
  }

  /* ── Message input ── */
  function bindMsgInput() {
    var inp = document.getElementById('dbsb-msg-text');
    if (!inp) return;
    inp.addEventListener('input', function () {
      state.addons.message.text = this.value;
      var cnt = document.getElementById('dbsb-msg-char-count');
      if (cnt) cnt.textContent = this.value.length;
    });
  }

  /* ── Gift input ── */
  function bindGiftInput() {
    var ta = document.getElementById('dbsb-gift-text');
    if (!ta) return;
    ta.addEventListener('input', function () { state.addons.gift.text = this.value; });
  }

  /* ── Candle number input ── */
  function bindCandleNumber() {
    var inp = document.getElementById('dbsb-candle-number');
    if (!inp) return;
    inp.addEventListener('input', function () {
      var cleaned = String(this.value || '').replace(/\D/g, '').slice(0, 2);
      if (cleaned !== this.value) this.value = cleaned;
      state.addons.candles.types.number.number = cleaned;
      updateSticky();
    });
  }

  /* ── Extra cards ── */
  function bindExtraCards() {
    document.querySelectorAll('.dbsb-extra-card').forEach(function (card) {
      card.addEventListener('click', function (e) {
        e.preventDefault(); e.stopPropagation();
        var id = this.dataset.extra;
        if (!state.extras[id]) return;
        state.extras[id].selected = !state.extras[id].selected;
        this.classList.toggle('is-selected', state.extras[id].selected);
        this.setAttribute('aria-checked', String(state.extras[id].selected));
        updateSticky();
      });
      card.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.click(); }
      });
    });
  }

  /* ── Cake-style cards (Birthday / Red Velvet / Carrot only) ──
     Single-select radio group. These cards share the .dbsb-extra-card
     class for styling, so bindExtraCards() also attaches a listener, but
     it early-returns on the missing data-extra (no-op) — this function
     does the real work. Paid options carry data-flavor (a
     designer_-_heart_cake_flavors variant title); that variant is pushed
     as the upcharge line. Free options have no data-flavor and only set
     the Cake style property. */
  function bindCakeStyleCards() {
    var cards = document.querySelectorAll('.dbsb-cakestyle-card');
    if (!cards.length) return;

    function selectCard(card, advance) {
      cards.forEach(function (c) {
        c.classList.remove('is-selected');
        c.setAttribute('aria-checked', 'false');
      });
      card.classList.add('is-selected');
      card.setAttribute('aria-checked', 'true');

      state.cakeStyle.value       = card.dataset.cakestyle || '';
      state.cakeStyle.flavorTitle = card.dataset.flavor || '';   // only paid options have this
      state.cakeStyle.price       = (parseInt(card.dataset.addonPrice, 10) || 0) / 100;

      var hidden = document.getElementById('dbsb-cakestyle-value');
      if (hidden) hidden.value = state.cakeStyle.value;

      markSectionDone('cakestyle');
      updateSticky();

      if (advance) { closeSection('cakestyle'); openSection('special'); }
    }

    cards.forEach(function (card) {
      card.addEventListener('click', function (e) {
        e.preventDefault(); e.stopPropagation();
        selectCard(this, true);
      });
      card.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.click(); }
      });
    });

    /* Initialize from the pre-selected (first) card so the property is set
       even if the customer never opens this step. No auto-advance on init. */
    var pre = document.querySelector('.dbsb-cakestyle-card.is-selected') || cards[0];
    if (pre) selectCard(pre, false);
  }

  /* ── Validation ── */
  function validateAddons() {
    var errors = [];

    if (state.addons.message.selected) {
      if (!state.addons.message.color) {
        errors.push({ id: 'dbsb-addon-card-message', msg: 'Please choose a plaque color for your message.' });
      } else if (!state.addons.message.text || !state.addons.message.text.trim()) {
        errors.push({ id: 'dbsb-addon-card-message', msg: 'Please enter the message for your plaque.' });
      }
    }

    if (state.addons.candles.selected) {
      var ts = state.addons.candles.types;
      var anyActive = ts.rainbow.active || ts.sparklers.active || ts.metallic.active || ts.number.active;
      if (!anyActive) {
        errors.push({ id: 'dbsb-addon-card-candles', msg: 'Please choose at least one candle type, or deselect candles.' });
      } else if (ts.metallic.active && !ts.metallic.color) {
        errors.push({ id: 'dbsb-addon-card-candles', msg: 'Please choose a color for your metallic candles.' });
      } else if (ts.number.active && !ts.number.color) {
        errors.push({ id: 'dbsb-addon-card-candles', msg: 'Please choose a color for your number candles.' });
      } else if (ts.number.active && (!ts.number.number || !ts.number.number.toString().trim())) {
        errors.push({ id: 'dbsb-addon-card-candles', msg: 'Please enter the number for your candles.' });
      }
    }

    if (state.addons.topper.selected && !state.addons.topper.color) {
      errors.push({ id: 'dbsb-addon-card-topper', msg: 'Please choose a color for your topper.' });
    }

    return errors;
  }

  function showValidationErrors(errors) {
    document.querySelectorAll('.dbsb-addon-error').forEach(function(el) {
      el.style.display = 'none'; el.textContent = '';
    });
    document.querySelectorAll('.dbsb-addon-card.has-error').forEach(function(el) {
      el.classList.remove('has-error');
    });

    if (errors.length === 0) return true;

    var first = errors[0];
    errors.forEach(function(err) {
      var card = document.getElementById(err.id);
      if (!card) return;
      card.classList.add('has-error');
      var errEl = card.querySelector('.dbsb-addon-error');
      if (!errEl) {
        errEl = document.createElement('div');
        errEl.className = 'dbsb-addon-error';
        card.appendChild(errEl);
      }
      errEl.textContent = err.msg;
      errEl.style.display = 'block';
    });

    var firstCard = document.getElementById(first.id);
    if (firstCard) {
      firstCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    return false;
  }

  /* ── Sticky ATC ── */
  function bindStickyBtn() {
    var btn = document.getElementById('dbsb-sticky-btn');
    if (!btn) return;
    btn.addEventListener('click', function (e) {
      e.preventDefault(); e.stopPropagation();
      if (!state.variantId) return;
      var errors = validateAddons();
      if (!showValidationErrors(errors)) return;
      submitCart();
    });
  }

  /* ── Cart — two-step submit matching designer pattern ──

     Step 1: Add the cake alone. Shopify returns the cake's unique
             cart line key (data.items[0].key) — this key changes
             every submit, so identical add-ons across multiple
             cakes can no longer merge into qty 2.

     Step 2: Build add-on line items, each tagged with that key as
             _addon_for. POST them as a single batch. If any add-on
             variant fails (e.g. unpublished, like the GF cupcake
             fee bug from yesterday), the cake stays in cart and
             we surface a specific error to the customer.

     Step 3: Verify — compare the builder's promised total to the
             actual cart total. Mismatch means an add-on silently
             failed to resolve; we console.warn so it's visible
             during testing but don't break checkout.

     This matches the designer cake builder. The trade-off vs.
     atomic single-shot is: failures here can leave the cake in
     cart with missing extras, but the customer sees a clear error
     and can refresh/retry. That's strictly better UX than the
     previous "silent merge into qty 2" behavior. */
  function submitCart() {
    var btn = document.getElementById('dbsb-sticky-btn');
    if (btn) { btn.disabled = true; btn.textContent = 'Adding...'; }

    var expectedTotal = calcTotal();

    /* Step 1: cake alone */
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        items: [{ id: state.variantId, quantity: 1, properties: buildProperties() }]
      })
    })
    .then(function (r) {
      return r.json().then(function (data) {
        return { ok: r.ok, status: r.status, data: data };
      });
    })
    .then(function (res) {
      if (!res.ok) {
        /* Cake itself failed — nothing in cart, reset and alert. */
        if (btn) { btn.textContent = 'Add to cart'; btn.disabled = false; }
        console.error('dbsb cake-add error:', res.status, res.data);
        var msg = (res.data && (res.data.description || res.data.message)) || 'Please try again.';
        alert('Something went wrong: ' + msg);
        return;
      }

      /* Cake is in cart — grab its key so add-ons can reference it. */
      var cakeKey = res.data && res.data.items && res.data.items[0] && res.data.items[0].key
                  ? res.data.items[0].key
                  : null;
      var addons = buildAddonItems(cakeKey);

      if (!addons.length) {
        /* No add-ons to submit — verify and finish. */
        verifyAndFinish(btn, expectedTotal);
        return;
      }

      /* Step 2: add-ons batched in one fetch, all tagged with cakeKey. */
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: addons })
      })
      .then(function (r) {
        return r.json().then(function (data) {
          return { ok: r.ok, status: r.status, data: data };
        });
      })
      .then(function (addonRes) {
        if (!addonRes.ok) {
          /* Add-ons failed — but cake is already in cart. Don't pretend
             everything's fine; tell the customer so they can decide
             whether to proceed or reach out. */
          console.error('dbsb addon-add error:', addonRes.status, addonRes.data);
          var amsg = (addonRes.data && (addonRes.data.description || addonRes.data.message)) || 'Some extras couldn\'t be added.';
          alert('Your cake was added, but: ' + amsg + ' Please review your cart.');
          /* Still navigate to cart so customer sees what landed. */
          if (btn) btn.textContent = 'Added (with issues)';
          setTimeout(function () { window.location.href = '/cart'; }, 600);
          return;
        }
        /* Both fetches succeeded — verify totals + finish. */
        verifyAndFinish(btn, expectedTotal);
      });
    })
    .catch(function (err) {
      console.error('dbsb cart error:', err);
      if (btn) { btn.textContent = 'Add to cart'; btn.disabled = false; }
      alert('We couldn\'t reach the cart. Check your connection and try again.');
    });
  }

  /* ── Verify cart total + redirect ──

     Pulls current cart contents and compares the line-price sum (for
     the just-added cake and its addons) to the builder's promised
     total. A mismatch >$0.02 means some add-on variant likely failed
     to resolve — we log it for debugging but don't block the customer.
     Then redirect to /cart so they can review.

     Designer uses this same pattern. */
  function verifyAndFinish(btn, expectedTotal) {
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        /* Find the most recently-added cake (last non-addon item) and
           its related addons (items whose _addon_for matches its key). */
        var cakeItems = cart.items.filter(function (i) {
          return !(i.properties && i.properties._addon_for);
        });
        var latestCake = cakeItems[cakeItems.length - 1];
        var cakeKey = latestCake ? latestCake.key : null;
        var related = cart.items.filter(function (i) {
          return i.key === cakeKey || (i.properties && i.properties._addon_for === cakeKey);
        });
        var actualTotal = related.reduce(function (s, i) { return s + (i.line_price / 100); }, 0);

        if (Math.abs(expectedTotal - actualTotal) > 0.02) {
          console.warn(
            'dbsb price mismatch — promised $' + expectedTotal.toFixed(2) +
            ', cart shows $' + actualTotal.toFixed(2) +
            ' (diff $' + (expectedTotal - actualTotal).toFixed(2) + '). ' +
            'Some add-on variant likely failed to resolve.'
          );
          console.log('dbsb cart lines for this cake:');
          related.forEach(function (i) {
            console.log('  · ' + i.product_title + ' — ' + (i.variant_title || '') + ' × ' + i.quantity + ' = $' + (i.line_price / 100).toFixed(2));
          });
        }
        onCartSuccess(btn);
      })
      .catch(function (err) {
        /* Verify is a nice-to-have, not blocking. If it fails just
           finish normally — customer's about to land on /cart anyway
           and will see whatever's actually there. */
        console.error('dbsb cart verify error:', err);
        onCartSuccess(btn);
      });
  }

  /* ── Cart success — flash, refresh badge, redirect ──

     Customer sees "Added! ✓" briefly so they get visual feedback that
     the action worked, then gets sent to /cart so they can review their
     order. Matches designer + cupcakes builders. */
  function onCartSuccess(btn) {
    if (btn) btn.textContent = 'Added! ✓';

    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('cart:change',  { bubbles: true }));

    /* Brief flash so the "Added! ✓" state registers visually before
       the page changes. Without this delay it feels like nothing
       happened — the page just changes. */
    setTimeout(function () { window.location.href = '/cart'; }, 400);
  }

  /* ── Build add-on line items ──

     Every add-on gets _addon_for: <cakeKey> in its properties. Since
     cakeKey is unique per submit (Shopify generates it), no two carts'
     add-ons will merge even if the variants + other properties match.

     If cakeKey is null (couldn't read it from response, edge case),
     we omit the _addon_for tag — those add-ons can still merge with
     siblings from other cakes, but the cart will at least have them.
     Better than silently dropping them. */
  function buildAddonItems(cakeKey) {
    var items = [];
    var tag = cakeKey ? { '_addon_for': cakeKey } : {};

    /* Cake-style flavor upcharge (Birthday: Funfetti / Rainbow). Free
       options (Red Velvet, Carrot) have no flavorTitle, so nothing is
       attached — their premium is already in the base price. */
    if (state.cakeStyle && state.cakeStyle.flavorTitle && state.cakeStyle.price > 0) {
      var fid = FLAVOR_IDS[state.cakeStyle.flavorTitle];
      if (fid) {
        items.push({
          id: fid,
          quantity: 1,
          properties: Object.assign({ '_addon': 'Cake style — ' + state.cakeStyle.value }, tag)
        });
      } else {
        console.warn('dbsb: flavor variant not found for cake style:', state.cakeStyle.flavorTitle);
      }
    }

    if (state.diet.gf && ADDON_IDS.glutenFree) {
      items.push({
        id: ADDON_IDS.glutenFree,
        quantity: 1,
        properties: Object.assign({ '_addon': 'Gluten-free' }, tag)
      });
    }
    if (state.addons.message.selected && state.addons.message.color) {
      var pid = PLAQUE_IDS[state.addons.message.color];
      if (pid) items.push({
        id: pid,
        quantity: 1,
        properties: Object.assign({
          'Message': state.addons.message.text,
          '_addon': 'Sugar plaque'
        }, tag)
      });
    }
    if (state.addons.candles.selected) {
      var ts = state.addons.candles.types;
      /* Each active type pushes its own cart line. Customer can have
         all 4 active simultaneously (rainbow + sparklers + metallic
         gold + number 5 silver) and each becomes a separate line.
         Mirrors designer cake builder. */
      if (ts.rainbow.active && ADDON_IDS.rainbowCandles) {
        items.push({
          id: ADDON_IDS.rainbowCandles,
          quantity: 1,
          properties: Object.assign({ '_addon': 'Rainbow candles' }, tag)
        });
      }
      if (ts.sparklers.active && ADDON_IDS.sparklers) {
        items.push({
          id: ADDON_IDS.sparklers,
          quantity: 1,
          properties: Object.assign({ '_addon': 'Sparklers' }, tag)
        });
      }
      if (ts.metallic.active && ts.metallic.color) {
        var mid = METALLIC_IDS[ts.metallic.color];
        if (mid) {
          items.push({
            id: mid,
            quantity: 1,
            properties: Object.assign({ '_addon': 'Metallic candles (' + ts.metallic.color + ')' }, tag)
          });
        } else {
          console.warn('dbsb: metallic candle variant not found for color:', ts.metallic.color);
        }
      }
      if (ts.number.active && ts.number.color && ts.number.number) {
        var nid = CANDLE_IDS[ts.number.color];
        var numDigits = String(ts.number.number).replace(/\D/g, '').length || 1;
        if (nid) {
          items.push({
            id: nid,
            quantity: numDigits,
            properties: Object.assign({
              'Number': ts.number.number,
              '_addon': 'Number candles (' + ts.number.color + ')'
            }, tag)
          });
        } else {
          console.warn('dbsb: number candle variant not found for color:', ts.number.color);
        }
      }
    }
    if (state.addons.topper.selected && state.addons.topper.color) {
      var tid = TOPPER_IDS[state.addons.topper.color];
      if (tid) items.push({
        id: tid,
        quantity: 1,
        properties: Object.assign({ '_addon': 'Birthday topper' }, tag)
      });
    }
    if (state.extras.bow.selected      && ADDON_IDS.bow) {
      items.push({
        id: ADDON_IDS.bow,
        quantity: 1,
        properties: Object.assign({ '_addon': 'XL Gold Bow' }, tag)
      });
    }
    if (state.extras.server.selected   && ADDON_IDS.cakeServer) {
      items.push({
        id: ADDON_IDS.cakeServer,
        quantity: 1,
        properties: Object.assign({ '_addon': 'Cake server' }, tag)
      });
    }
    if (state.extras.coldpack.selected && ADDON_IDS.coldPack) {
      items.push({
        id: ADDON_IDS.coldPack,
        quantity: 1,
        properties: Object.assign({ '_addon': 'Gel cold pack' }, tag)
      });
    }
    return items;
  }

  function buildProperties() {
    var props = {};
    if (state.cakeStyle && state.cakeStyle.value) props['Cake style'] = state.cakeStyle.value;
    var dietParts = [];
    if (state.diet.gf) dietParts.push('Gluten-Free (+$15.00)');
    if (state.diet.nut) dietParts.push('Nut Allergy (acknowledged)');
    if (dietParts.length > 0) props['Dietary'] = dietParts.join(' + ');
    if (state.addons.message.selected) props['Message plaque'] = state.addons.message.color + ' — ' + state.addons.message.text;
    if (state.addons.candles.selected) {
      var ts2 = state.addons.candles.types;
      var parts = [];
      if (ts2.rainbow.active)   parts.push('Rainbow candles');
      if (ts2.sparklers.active) parts.push('Sparklers');
      if (ts2.metallic.active && ts2.metallic.color) parts.push('Metallic candles (' + ts2.metallic.color + ')');
      if (ts2.number.active && ts2.number.color && ts2.number.number) {
        parts.push('Number candles (' + ts2.number.color + ') — #' + ts2.number.number);
      }
      if (parts.length) props['Candles'] = parts.join(' + ');
    }
    if (state.addons.topper.selected)    props['Topper color']  = state.addons.topper.color;
    if (state.addons.gift.text && state.addons.gift.text.trim()) props['Gift message'] = state.addons.gift.text;
    if (state.extras.bow.selected)       props['XL Gold Bow']   = 'Yes';
    if (state.extras.server.selected)    props['Cake server']   = 'Yes';
    if (state.extras.coldpack.selected)  props['Gel cold pack'] = 'Yes';
    return props;
  }

  /* ── Sticky update ── */
  function calcTotal() {
    var total = state.basePrice;
    if (state.cakeStyle && state.cakeStyle.price) total += state.cakeStyle.price;
    if (state.diet.gf) total += 15;
    if (state.addons.message.selected)  total += 7.50;
    if (state.addons.topper.selected)   total += 7.50;
    if (state.addons.candles.selected) {
      /* Sum every active type. Rainbow + sparklers + metallic + number
         can all be active at once. Each adds its own price. */
      var ts3 = state.addons.candles.types;
      if (ts3.rainbow.active)   total += 4.99;
      if (ts3.sparklers.active) total += 11.99;
      if (ts3.metallic.active && ts3.metallic.color) total += 4.99;
      if (ts3.number.active && ts3.number.color && ts3.number.number) {
        var digits = String(ts3.number.number).replace(/\D/g, '').length || 0;
        total += digits * 2;
      }
    }
    Object.keys(state.extras).forEach(function (k) {
      if (state.extras[k].selected) total += state.extras[k].price;
    });
    return total;
  }

  function updateSticky() {
    var total   = calcTotal();
    var nameEl  = document.getElementById('dbsb-sticky-name');
    var priceEl = document.getElementById('dbsb-sticky-price');
    var liveEl  = document.getElementById('dbsb-sticky-live');
    var btn     = document.getElementById('dbsb-sticky-btn');

    if (nameEl)  nameEl.textContent  = state.sizeName || 'Choose your size above';
    if (priceEl) priceEl.textContent = state.basePrice > 0 ? '$' + total.toFixed(2) : 'From $—';
    if (liveEl && state.sizeName) liveEl.textContent = state.sizeName + ' — $' + total.toFixed(2);
    if (btn) {
      if (state.variantId) {
        btn.classList.add('is-active');
        btn.textContent = btn.textContent === 'Add to cart' || btn.textContent === 'Added! ✓' ? btn.textContent : 'Add to cart';
      } else {
        btn.textContent = 'Add to cart';
      }
    }
  }

  /* ── Section done marker ── */
  function markSectionDone(id) {
    var num = document.getElementById('dbsb-num-' + id);
    if (num) num.classList.add('is-done');
  }

  /* ── Sync hidden variant input ── */
  function syncVariantInput() {
    var input = document.getElementById('dbsb-variant-id');
    if (input) input.value = state.variantId;
  }

})();

/* ── Cart badge count update ──
   Single source of truth for keeping the .dbn-cart-badge count
   accurate. Was previously duplicated across two IIFEs PLUS a 2s
   setInterval polling loop — that's now consolidated to one
   listener. The badge updates on:
     - Page load
     - cart:change / cart:refresh / cart-update events fired by Dawn
     - cart:change events fired by this builder after a successful add
   No polling. If a future Dawn event fails to fire, add it to the
   EVENTS array below — don't reach for setInterval. */
(function () {
  var EVENTS = ['cart:refresh', 'cart:change', 'cart:update', 'cart-update'];
  var lastCount = -1;

  function refreshBadge() {
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        /* Count only non-addon items (cakes). Items with an _addon
           property are decorative add-ons, not separate cake orders. */
        var cakeCount = 0;
        cart.items.forEach(function (item) {
          var isAddon = false;
          if (item.properties) {
            Object.keys(item.properties).forEach(function (key) {
              if (key === '_addon' || key === '_addon_for') isAddon = true;
            });
          }
          if (!isAddon) cakeCount += item.quantity;
        });

        if (cakeCount === lastCount) return;
        lastCount = cakeCount;

        var badges = document.querySelectorAll('.dbn-cart-badge');
        badges.forEach(function (el) {
          el.textContent = cakeCount;
          el.style.display = cakeCount > 0 ? '' : 'none';
        });
        var bubbles = document.querySelectorAll('.cart-count-bubble span:not(.visually-hidden)');
        bubbles.forEach(function (el) { el.textContent = cakeCount; });
      })
      .catch(function () { /* silent fail — badge stays at last known count */ });
  }

  if (document.readyState === 'complete') {
    refreshBadge();
  } else {
    window.addEventListener('load', refreshBadge);
  }
  EVENTS.forEach(function (evt) {
    document.addEventListener(evt, refreshBadge);
  });
})();